// Shared utility functions for receivables & partner suspension
// Story 46-8: Emlékeztető & Felfüggesztés UI

/** Reminder escalation level (matches backend PaymentReminderService) */
export type ReminderLevel = 'FIRST' | 'SECOND' | 'FINAL';

/** Reminder level metadata */
export interface ReminderLevelInfo {
  level: ReminderLevel;
  label: string;
  description: string;
  tone: 'polite' | 'firm' | 'warning';
}

/** Suspension eligibility result */
export interface SuspensionEligibility {
  eligible: boolean;
  reason: string;
}

/** Days thresholds matching backend REMINDER_THRESHOLDS */
export const REMINDER_THRESHOLDS = {
  FIRST: 7,
  SECOND: 14,
  FINAL: 30,
} as const;

/** Days threshold matching backend SUSPENSION_THRESHOLD_DAYS */
export const SUSPENSION_THRESHOLD_DAYS = 45;

/**
 * Get the appropriate reminder level for a given number of overdue days.
 * Returns null if days overdue is below the first threshold.
 * Matches backend PaymentReminderService.determineSuggestedLevel logic.
 */
export function getReminderLevel(daysOverdue: number): ReminderLevel | null {
  if (daysOverdue < REMINDER_THRESHOLDS.FIRST) {
    return null;
  }
  if (daysOverdue >= REMINDER_THRESHOLDS.FINAL) {
    return 'FINAL';
  }
  if (daysOverdue >= REMINDER_THRESHOLDS.SECOND) {
    return 'SECOND';
  }
  return 'FIRST';
}

/**
 * Get display info for a reminder level.
 */
export function getReminderLevelInfo(level: ReminderLevel): ReminderLevelInfo {
  switch (level) {
    case 'FIRST':
      return {
        level: 'FIRST',
        label: '1. emlékeztető',
        description: 'Udvarias fizetési emlékeztető',
        tone: 'polite',
      };
    case 'SECOND':
      return {
        level: 'SECOND',
        label: '2. felszólítás',
        description: 'Határozott fizetési felszólítás',
        tone: 'firm',
      };
    case 'FINAL':
      return {
        level: 'FINAL',
        label: 'Végleges felszólítás',
        description: 'Végső fizetési felszólítás — felfüggesztés figyelmeztetéssel',
        tone: 'warning',
      };
  }
}

/**
 * Check if a partner is eligible for suspension based on overdue days.
 * VIP partners require manual review (cannot be auto-suspended).
 * Non-VIP partners can be suspended after SUSPENSION_THRESHOLD_DAYS.
 */
export function checkSuspensionEligibility(
  daysOverdue: number,
  isVip: boolean
): SuspensionEligibility {
  if (isVip) {
    return {
      eligible: false,
      reason: 'VIP partner — manuális döntés szükséges',
    };
  }

  if (daysOverdue < SUSPENSION_THRESHOLD_DAYS) {
    return {
      eligible: false,
      reason: `Csak ${SUSPENSION_THRESHOLD_DAYS} nap késés felett felfüggeszthető. Jelenlegi: ${daysOverdue} nap.`,
    };
  }

  return {
    eligible: true,
    reason: `${daysOverdue} napos késés — felfüggesztés engedélyezett`,
  };
}

/**
 * Get CSS classes for reminder level badges.
 */
export function getReminderBadgeClasses(level: ReminderLevel): string {
  switch (level) {
    case 'FIRST':
      return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    case 'SECOND':
      return 'bg-orange-100 text-orange-800 border-orange-300';
    case 'FINAL':
      return 'bg-red-100 text-red-800 border-red-300';
  }
}

/**
 * Get reminder level from aging bucket's minDays.
 * Used to determine which level badge to show in the receivables view.
 */
export function getReminderLevelForBucket(bucketMinDays: number): ReminderLevel | null {
  return getReminderLevel(bucketMinDays);
}

// ── T-71-24 (a): a `GET /dashboard/receivables/aging` VALÓS válasz-alakja ──────────────────
// SSoT: apps/kgc-api/src/modules/dashboard/receivables/dto/receivables-aging.dto.ts
// (`AgingReportData`). A korábbi FE-típus egy soha nem létezett alakot várt
// (`overduePercentage`, `buckets[].partners`), ezért az oldal `toFixed`-en omlott el.
// A kulcs-egyezést a `ReceivablesPage.contract.spec.ts` méri a DTO-forrás ellen.

export type AgingBucketLabel = '0-30' | '31-60' | '61-90' | '90+';

export interface AgingInvoiceDto {
  id: string;
  invoiceNumber: string;
  partnerId: string;
  partnerName: string;
  dueDate: string;
  balanceDue: number;
  daysOverdue: number;
}

export interface AgingBucketDto {
  label: AgingBucketLabel;
  count: number;
  totalAmount: number;
  invoices?: AgingInvoiceDto[] | undefined;
}

export interface TopDebtorDto {
  partnerId: string;
  partnerName: string;
  totalDebt: number;
  invoiceCount: number;
  oldestDueDate: string;
}

export interface AgingReportDto {
  generatedAt: string;
  totalReceivables: number;
  buckets: AgingBucketDto[];
  topDebtors: TopDebtorDto[];
}

/** Egy partner összesítése EGY sávon belül — a sáv `invoices[]`-éből képezve. */
export interface AgingPartnerView {
  partnerId: string;
  partnerName: string;
  amount: number;
  invoiceCount: number;
  /** A partner sávbeli legrégebbi esedékessége (ISO). */
  oldestDueDate: string;
  /** ⚠️ A BE ma NEM adja — a `buildAgingView` sosem tölti; a felfüggesztés-UI ágai valós adaton holtak. */
  status?: 'ACTIVE' | 'INACTIVE' | 'BLACKLISTED' | undefined;
}

export interface AgingBucketView {
  label: AgingBucketLabel;
  /** A sáv alsó határa napban — az emlékeztető/felfüggesztés küszöbe ehhez mér. */
  minDays: number;
  count: number;
  totalAmount: number;
  partners: AgingPartnerView[];
}

export interface AgingView {
  generatedAt: string;
  totalReceivables: number;
  /** A `daysOverdue > 0` számlák egyenlege — a BE a le nem járt számlát a `0-30` sávba teszi `0` nappal. */
  overdueReceivables: number;
  /** `0`, ha nincs kintlévőség (nincs osztás nullával). */
  overduePercentage: number;
  buckets: AgingBucketView[];
}

const BUCKET_MIN_DAYS: Record<AgingBucketLabel, number> = {
  '0-30': 0,
  '31-60': 31,
  '61-90': 61,
  '90+': 91,
};

function groupPartners(invoices: AgingInvoiceDto[]): AgingPartnerView[] {
  const byPartner = new Map<string, AgingPartnerView>();
  for (const inv of invoices) {
    const existing = byPartner.get(inv.partnerId);
    if (existing) {
      existing.amount += inv.balanceDue;
      existing.invoiceCount += 1;
      if (inv.dueDate < existing.oldestDueDate) existing.oldestDueDate = inv.dueDate;
    } else {
      byPartner.set(inv.partnerId, {
        partnerId: inv.partnerId,
        partnerName: inv.partnerName,
        amount: inv.balanceDue,
        invoiceCount: 1,
        oldestDueDate: inv.dueDate,
      });
    }
  }
  return [...byPartner.values()].sort((a, b) => b.amount - a.amount);
}

/** A BE aging-riportból az oldal nézete: késedelmes összeg/arány + sávonkénti partner-bontás. */
export function buildAgingView(report: AgingReportDto): AgingView {
  const allInvoices = report.buckets.flatMap(b => b.invoices ?? []);
  const overdueReceivables = allInvoices
    .filter(inv => inv.daysOverdue > 0)
    .reduce((sum, inv) => sum + inv.balanceDue, 0);
  const overduePercentage =
    report.totalReceivables > 0 ? (overdueReceivables / report.totalReceivables) * 100 : 0;

  return {
    generatedAt: report.generatedAt,
    totalReceivables: report.totalReceivables,
    overdueReceivables,
    overduePercentage,
    buckets: report.buckets.map(b => ({
      label: b.label,
      minDays: BUCKET_MIN_DAYS[b.label],
      count: b.count,
      totalAmount: b.totalAmount,
      partners: groupPartners(b.invoices ?? []),
    })),
  };
}
